/**
 * Parsing + sheet classification for the upload step (wmi_plan §3/§8).
 *
 * The browser owns parsing (Workers have no DOMParser); the raw file never
 * leaves the device. An `.xlsx` is one workbook of many sheets; CSVs arrive as
 * one file per sheet. Both converge on a list of {@link DetectedSheet}s that
 * carry the low-level {@link RawSheet} (so xlsx numeric-cell provenance
 * survives) plus the migration sheet-kind detected by name or header shape.
 *
 * Pure except for the async `readXlsx` DEFLATE step; no fetch, no React. The
 * screen reads each `File` into bytes/text and calls these.
 */
import {
  readXlsx,
  readCsvToSheet,
  normalizeHeaderKey,
  SpreadsheetError,
  type RawSheet,
  type SpreadsheetErrorCode,
} from '@jumpstone/spreadsheet';
import { MIGRATION_DATA_SHEETS } from '@jumpstone/core';
import type {
  MigrationStageRow,
  MigrationStageSheets,
} from '../../../data/use_wagepro_migration.js';

/** The four importable sheet kinds. */
export type MigrationSheetKind = (typeof MIGRATION_DATA_SHEETS)[number];

export interface DetectedSheet {
  /** Classified kind, or null for an unrecognised / Instructions / Lists tab. */
  kind: MigrationSheetKind | null;
  sourceName: string;
  headers: string[];
  rawSheet: RawSheet;
}

/** A file the user dropped, already read by the screen. */
export type RawUpload =
  | { name: string; format: 'xlsx'; bytes: Uint8Array }
  | { name: string; format: 'csv'; text: string };

export type ParseUploadsResult =
  | { outcome: 'success'; sheets: DetectedSheet[] }
  | { outcome: 'failure'; code: SpreadsheetErrorCode | 'unknown' };

/** Guess the format from a filename extension. */
export function detectFormat(filename: string): 'xlsx' | 'csv' | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.xlsx')) return 'xlsx';
  if (lower.endsWith('.csv')) return 'csv';
  return null;
}

/** 0-based column index → Excel letter (0→A, 25→Z, 26→AA …). */
function colLetter(idx: number): string {
  let name = '';
  let n = idx + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    name = String.fromCharCode(65 + m) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

const NORMALIZED_SHEET_NAMES = new Map<string, MigrationSheetKind>(
  MIGRATION_DATA_SHEETS.map((name) => [normalizeHeaderKey(name), name]),
);

/**
 * Classify a sheet by its tab name first (exact normalized match), then by its
 * header shape — the distinguishing columns each sheet carries. Returns null
 * for Instructions / Lists / an unrecognised flat export (the user reassigns
 * it in the UI).
 */
export function detectSheetKind(
  sourceName: string,
  headers: string[],
): MigrationSheetKind | null {
  const byName = NORMALIZED_SHEET_NAMES.get(normalizeHeaderKey(sourceName));
  if (byName) return byName;

  const normalized = new Set(headers.map((h) => normalizeHeaderKey(h)));
  const has = (col: string): boolean => normalized.has(col);

  if (has('period_start') || has('period_end') || has('gross_pay')) {
    return 'Payroll_History';
  }
  if (has('leave_type_code') || has('balance_days')) return 'Leave_Balances';
  if (has('effective_from')) return 'Salary_History';
  if (
    has('full_name') ||
    has('employment_start_date') ||
    has('employment_type')
  ) {
    return 'Employees';
  }
  return null;
}

function toDetectedSheet(rawSheet: RawSheet): DetectedSheet {
  const headers = (rawSheet.grid.at(0) ?? []).map((h) => h.trim());
  return {
    kind: detectSheetKind(rawSheet.name, headers),
    sourceName: rawSheet.name,
    headers,
    rawSheet,
  };
}

/** Parse every uploaded file into classified sheets. Never throws. */
export async function parseUploads(
  uploads: RawUpload[],
): Promise<ParseUploadsResult> {
  const sheets: DetectedSheet[] = [];
  try {
    for (const upload of uploads) {
      if (upload.format === 'xlsx') {
        const workbook = await readXlsx(upload.bytes, {
          formulaWithoutValue: 'error',
        });
        for (const rawSheet of workbook.sheets) {
          sheets.push(toDetectedSheet(rawSheet));
        }
      } else {
        // One CSV = one sheet named after the file (sans extension).
        const name = upload.name.replace(/\.csv$/i, '');
        sheets.push(toDetectedSheet(readCsvToSheet(name, upload.text)));
      }
    }
    return { outcome: 'success', sheets };
  } catch (err) {
    if (err instanceof SpreadsheetError) {
      return { outcome: 'failure', code: err.code };
    }
    console.error('[wagepro-web] migration upload parse failed:', err);
    return { outcome: 'failure', code: 'unknown' };
  }
}

/**
 * Build one sheet's stage rows from a {@link RawSheet}. `mapping` maps a FILE
 * header to a target migration field name (identity when the headers already
 * match). Columns mapped to `''` (ignore) are dropped. Each row carries the
 * Excel row number the user sees (header = 1) and the names of any TARGET
 * columns whose source cell was a raw number (xlsx id/account precision loss).
 */
export function sheetToStageRows(
  rawSheet: RawSheet,
  mapping?: Record<string, string>,
): MigrationStageRow[] {
  const headers = (rawSheet.grid.at(0) ?? []).map((h) => h.trim());
  const targetOf = (fileHeader: string): string | null => {
    if (!mapping) return fileHeader;
    // Header not in the map → keep the file header as-is; '' → ignore the column.
    if (!Object.hasOwn(mapping, fileHeader)) return fileHeader;
    const mapped = mapping[fileHeader];
    return mapped.length === 0 ? null : mapped;
  };

  const rows: MigrationStageRow[] = [];
  for (let gridRow = 1; gridRow < rawSheet.grid.length; gridRow++) {
    const row = rawSheet.grid[gridRow];
    const excelRow = gridRow + 1; // grid[0] is Excel row 1 (header).
    const cells: Record<string, string> = {};
    const numericCells: string[] = [];
    headers.forEach((fileHeader, col) => {
      const target = targetOf(fileHeader);
      if (target === null) return;
      const value = row.at(col) ?? '';
      cells[target] = value;
      if (rawSheet.numericCells.has(`${colLetter(col)}${String(excelRow)}`)) {
        numericCells.push(target);
      }
    });
    rows.push(
      numericCells.length > 0
        ? { row: excelRow, cells, numericCells }
        : { row: excelRow, cells },
    );
  }
  return rows;
}

/**
 * Assemble the `stage` payload from classified sheets. The LAST sheet of each
 * kind wins (a workbook never has two, and a re-added CSV should replace, not
 * duplicate). `mappings` is an optional per-source-name header map. Returns
 * null when no Employees sheet was classified — the importer requires one.
 */
export function buildStageSheets(
  sheets: DetectedSheet[],
  mappings?: Record<string, Record<string, string>>,
): MigrationStageSheets | null {
  const employees = pickSheet(sheets, 'Employees');
  const payroll = pickSheet(sheets, 'Payroll_History');
  // An Employees sheet is required UNLESS this is a payroll-history-only import
  // for employees already in WagePro (decision 5 — the folded-in CSV importer).
  if (!employees && !payroll) return null;
  const mapFor = (s: DetectedSheet): Record<string, string> | undefined =>
    mappings?.[s.sourceName];

  const result: MigrationStageSheets = {
    employees: employees
      ? sheetToStageRows(employees.rawSheet, mapFor(employees))
      : [],
  };
  const salary = pickSheet(sheets, 'Salary_History');
  if (salary) {
    result.salary_history = sheetToStageRows(salary.rawSheet, mapFor(salary));
  }
  const leave = pickSheet(sheets, 'Leave_Balances');
  if (leave) {
    result.leave_balances = sheetToStageRows(leave.rawSheet, mapFor(leave));
  }
  if (payroll) {
    result.payroll_history = sheetToStageRows(
      payroll.rawSheet,
      mapFor(payroll),
    );
  }
  return result;
}

/**
 * Accumulate sheets across SEPARATE drops (e.g. the user drops one CSV per
 * gesture): incoming sheets are appended to the existing set, with a same
 * `sourceName` REPLACING its earlier version (a re-dropped, corrected file
 * wins). Without this a second drop would wipe the first (F6).
 */
export function mergeDetectedSheets(
  existing: readonly DetectedSheet[],
  incoming: readonly DetectedSheet[],
): DetectedSheet[] {
  const bySource = new Map<string, DetectedSheet>();
  for (const s of existing) bySource.set(s.sourceName, s);
  for (const s of incoming) bySource.set(s.sourceName, s);
  return [...bySource.values()];
}

function pickSheet(
  sheets: DetectedSheet[],
  kind: MigrationSheetKind,
): DetectedSheet | undefined {
  return sheets.filter((s) => s.kind === kind).at(-1);
}
